<script setup lang="ts">
const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string }>();

const availableActions = ["view", "download", "copy", "delete"] as const;
type SiteFileAction = (typeof availableActions)[number];

const { field } = useInlineBlockEditor(props.id);
const route = useRoute();
const injectedReportUserId = inject<{ value?: string } | null>("reportUserId", null);

const containerId = field<string>("containerId", "");
const containerPath = field<string>("containerPath", "/");
const includeMimeTypes = field<string[]>("includeMimeTypes", []);
const excludeMimeTypes = field<string[]>("excludeMimeTypes", []);
const actionFilter = field<SiteFileAction[]>("actionFilter", [...availableActions]);
const enableNavigation = field<boolean>("enableNavigation", true);
const showBackButton = field<boolean>("showBackButton", true);
const showDropZone = field<boolean>("showDropZone", true);
const mkdirOnUpload = field<boolean>("mkdirOnUpload", true);
const editorCandidateId = computed(() =>
  injectedReportUserId?.value
  ?? (typeof route.query.user === "string" ? route.query.user : null),
);

function formatMimeTypes(value: unknown) {
  return Array.isArray(value) ? value.join("\n") : "";
}

function parseMimeTypes(value: string) {
  return value
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
}

const includeMimeTypesText = computed({
  get: () => formatMimeTypes(includeMimeTypes.value),
  set: (value: string) => {
    includeMimeTypes.value = parseMimeTypes(value);
  },
});

const excludeMimeTypesText = computed({
  get: () => formatMimeTypes(excludeMimeTypes.value),
  set: (value: string) => {
    excludeMimeTypes.value = parseMimeTypes(value);
  },
});

function normalizeCheckboxValue(value: boolean | "indeterminate") {
  return value === true;
}

function toggleAction(action: SiteFileAction, checked: boolean) {
  if (checked) {
    if (!actionFilter.value.includes(action)) {
      actionFilter.value = [...actionFilter.value, action];
    }
    return;
  }

  actionFilter.value = actionFilter.value.filter((item) => item !== action);
}
</script>

<template>
  <EditorSection :title="t('title')" icon="lucide:upload" accent="media">
    <EditorField :label="t('containerIdLabel')">
      <Input
        v-model="containerId"
        class="h-8 rounded-sm bg-muted text-[13px]"
        placeholder="my-container"
      />
    </EditorField>

    <EditorField :label="t('pathLabel')" :hint="t('templateHint')">
      <EditorPathInput
        v-model="containerPath"
        mode="existing-folder"
        :scopes="['user']"
        default-scope="user"
        :user-id="editorCandidateId"
        placeholder="/data/<userId>/some/subfolder"
        aria-label="Upload path"
      />
    </EditorField>

    <EditorField :label="t('includeMimeTypesLabel')" :hint="t('mimeTypesHint')">
      <Textarea
        v-model="includeMimeTypesText"
        class="min-h-20 rounded-sm bg-muted text-[13px]"
        :placeholder="t('mimeTypesPlaceholder')"
      />
    </EditorField>

    <EditorField :label="t('excludeMimeTypesLabel')">
      <Textarea
        v-model="excludeMimeTypesText"
        class="min-h-20 rounded-sm bg-muted text-[13px]"
        :placeholder="t('mimeTypesPlaceholder')"
      />
    </EditorField>

    <EditorField :label="t('actionsLabel')">
      <div class="grid grid-cols-2 gap-2">
        <label
          v-for="action in availableActions"
          :key="action"
          class="flex items-center gap-2 rounded-sm border bg-muted/40 px-2 py-1.5 text-[12.5px] capitalize"
        >
          <Checkbox
            :model-value="actionFilter.includes(action)"
            @update:model-value="(checked) => toggleAction(action, normalizeCheckboxValue(checked))"
          />
          {{ action }}
        </label>
      </div>
    </EditorField>

    <EditorField :label="t('enableNavigationLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("enableNavigationHint") }}</span>
        <Switch v-model="enableNavigation" />
      </div>
    </EditorField>

    <EditorField :label="t('showBackButtonLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("showBackButtonHint") }}</span>
        <Switch v-model="showBackButton" />
      </div>
    </EditorField>

    <EditorField :label="t('showDropZoneLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("showDropZoneHint") }}</span>
        <Switch v-model="showDropZone" />
      </div>
    </EditorField>

    <EditorField :label="t('mkdirOnUploadLabel')">
      <div class="flex items-center justify-between gap-3">
        <span class="text-[12.5px] text-muted-foreground">{{ t("mkdirOnUploadHint") }}</span>
        <Switch v-model="mkdirOnUpload" />
      </div>
    </EditorField>
  </EditorSection>
</template>

<i18n lang="yaml">
en:
  title: "File Upload"
  containerIdLabel: "Container ID"
  pathLabel: "Path"
  templateHint: "Candidate-scoped storage. Existing legacy paths remain supported."
  includeMimeTypesLabel: "Include MIME Types"
  excludeMimeTypesLabel: "Exclude MIME Types"
  mimeTypesPlaceholder: |-
    image/*
    application/pdf
  mimeTypesHint: "One MIME type per line. Wildcards like image/* are supported."
  actionsLabel: "Allowed Actions"
  enableNavigationLabel: "Enable Navigation"
  enableNavigationHint: "Allow browsing through folders."
  showBackButtonLabel: "Show Back Button"
  showBackButtonHint: "Show navigation back control."
  showDropZoneLabel: "Show Drop Zone"
  showDropZoneHint: "Display the upload drop zone."
  mkdirOnUploadLabel: "Create Missing Parent Folders"
  mkdirOnUploadHint: "Create parent folders during upload."
</i18n>
